import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApiClient, Auftrag } from '../api';
import { statusAnzeige } from '../auftragsstatus';
import { vorWieLange } from './Zustandsseite';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, StatusBadge, type Spalte,
} from '../muster';

/**
 * MP-6 (F324): die Auftraege des aktiven Projekts. Der Server bindet `GET /api/jobs?projectId=` an die Datenbank des
 * Projekts; ohne projectId saehe man nur die Dienst-DB (Projekte mit eigener DB fehlten still). Deshalb fragt die
 * Liste NIE ohne Projekt: `undefined` = die Projektliste laedt noch (warten), `null` = kein Projekt (leere Liste).
 */
export function Auftraege({ api, projektId }: { api: ApiClient; projektId: string | null | undefined }) {
  const [produktion, setProduktion] = useState<Auftrag[] | null>(null);
  const [golden, setGolden] = useState<Auftrag[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [goldenFehler, setGoldenFehler] = useState<string | null>(null);
  const navigiere = useNavigate();

  const auftraege = useMemo(() => [...(produktion ?? []), ...golden]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  [produktion, golden]);

  const laden = useCallback(async (aktiv: () => boolean) => {
    if (projektId === undefined) return;
    if (projektId === null) {
      if (aktiv()) { setProduktion([]); setGolden([]); setFehler(null); }
      return;
    }

    try {
      const daten = await api.auftraege(200, projektId);
      if (aktiv()) { setProduktion(daten); setFehler(null); }
    } catch (e) {
      if (aktiv()) setFehler(e);
    }

    try {
      const daten = await api.goldenAuftraege(200, projektId);
      if (aktiv()) { setGolden(daten); setGoldenFehler(null); }
    } catch (e) {
      if (aktiv()) {
        setGolden([]);
        setGoldenFehler(e instanceof Error ? e.message : String(e));
      }
    }
  }, [api, projektId]);

  useEffect(() => {
    let aktiv = true;
    const istAktiv = () => aktiv;
    // Projektwechsel: die Liste des alten Projekts nicht stehen lassen, bis die neue da ist.
    setProduktion(null);
    setGolden([]);

    laden(istAktiv);
    // Seltener als auf der Zustandsseite: Die Liste aendert sich nur, wenn ein
    // Auftrag beginnt oder endet.
    const takt = setInterval(() => laden(istAktiv), 20_000);

    return () => { aktiv = false; clearInterval(takt); };
  }, [laden]);

  const spalten: Spalte<Auftrag>[] = useMemo(() => [
    {
      schluessel: 'nummer', titel: 'Nummer', breite: '132px', immerSichtbar: true,
      wert: (a) => a.jobNumber,
      inhalt: (a) => <strong>{a.jobNumber}</strong>,
    },
    {
      schluessel: 'umgebung', titel: 'Umgebung', breite: '120px',
      wert: (a) => (a.environment === 'GOLDEN' ? 'Golden / AI' : 'Produktion'),
      inhalt: (a) => (a.environment === 'GOLDEN'
        ? <StatusBadge zustand="warnung" text="Golden / AI" klein />
        : <StatusBadge zustand="neutral" text="Produktion" klein />),
    },
    {
      schluessel: 'art', titel: 'Art',
      wert: (a) => a.workload ?? '',
      inhalt: (a) => (
        <span title={a.workload ?? undefined}>
          {a.workload || '—'}
          {a.caseId && <><br /><span className="sehr-schwach">{a.caseId}</span></>}
        </span>
      ),
    },
    {
      schluessel: 'modell', titel: 'Modell', breite: '260px',
      wert: (a) => a.modelle ?? '',
      inhalt: (a) => (a.modelle
        ? (
          <span
            title={a.modelle}
            style={{
              display: 'block', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {a.modelle}
          </span>
        )
        : <span className="sehr-schwach">—</span>),
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '150px',
      // UI-A73: der Wartegrund des Servers statt „läuft“, wenn kein Teil vergeben werden kann.
      wert: (a) => statusAnzeige(a.status, a.wartegrund).text,
      inhalt: (a) => {
        const s = statusAnzeige(a.status, a.wartegrund);
        return (
          <>
            <StatusBadge zustand={s.zustand} text={s.text} titel={s.titel} klein />
            {a.acceptanceStatus && (
              <><br /><span className="sehr-schwach">Abnahme: {a.acceptanceStatus}</span></>
            )}
          </>
        );
      },
    },
    {
      schluessel: 'maschine', titel: 'Maschine',
      wert: (a) => a.maschine ?? '',
      inhalt: (a) => (a.maschine || <span className="sehr-schwach">wartet</span>),
    },
    {
      schluessel: 'beauftragt', titel: 'Beauftragt', art: 'datum', breite: '140px',
      // Sortiert wird nach dem Zeitpunkt, angezeigt "vor 3 min".
      wert: (a) => a.createdAt,
      inhalt: (a) => <span title={a.createdAt}>{vorWieLange(a.createdAt)}</span>,
    },
    {
      schluessel: 'von', titel: 'Von',
      wert: (a) => a.requestedByEmail ?? '',
      inhalt: (a) => a.requestedByEmail ?? '—',
    },
    {
      schluessel: 'dauer', titel: 'Dauer', ausrichtung: 'rechts', breite: '90px',
      // Sortierschlüssel sind Sekunden, sonst stünde "9 min" vor "10 min".
      wert: (a) => dauerSekunden(a),
      inhalt: (a) => dauer(a),
    },
    {
      schluessel: 'hinweise', titel: 'Hinweise', breite: '100px',
      wert: (a) => a.warningCount,
      inhalt: (a) => (a.warningCount > 0
        ? <StatusBadge zustand="warnung" text={`${a.warningCount}`} klein
                       titel={`${a.warningCount} Hinweise`} />
        : <span className="sehr-schwach">—</span>),
    },
  ], []);

  return (
    <>
      <PageHeader
        titel="Aufträge"
        beschreibung={'Die fortlaufenden Auftragsnummern bleiben als unveränderlicher '
          + 'Nachweis ihrer Umgebung erhalten. Produktion und Golden/AI sind hier '
          + 'gemeinsam nach Beauftragungszeit eingeordnet.'}
        meta={produktion !== null && (
          <>{auftraege.length} Aufträge · davon {golden.length} Golden/AI</>
        )}
      />

      {goldenFehler && (
        <div className="hinweis warnung">
          Golden-/AI-Aufträge sind vorübergehend nicht erreichbar: {goldenFehler}
        </div>
      )}

      <DataTable
        name="Aufträge"
        spalten={spalten}
        zeilen={auftraege}
        schluessel={(a) => `${a.environment ?? 'PRODUCTION'}:${a.id}`}
        laedt={produktion === null && fehler === null}
        fehler={fehler}
        beiErneutVersuchen={() => { setFehler(null); laden(() => true); }}
        ausfuhr
        suchePlatzhalter="Nummer, Modell, Besteller …"
        sortierung={{ spalte: 'beauftragt', richtung: 'ab' }}
        beiZeileKlick={(a) => {
          const ziel = zielRoute(a);
          if (ziel) navigiere(ziel);
        }}
        zeilenKlasse={(a) => (zielRoute(a) ? 'klickbar' : undefined)}
        leer={(
          <EmptyState
            titel="Noch kein Auftrag beauftragt."
            erklaerung={'Aufträge entstehen aus einer Modellaufnahme. Wählen Sie ein '
              + 'Projekt und starten Sie dort einen Export.'}
          />
        )}
      />
    </>
  );
}

function zielRoute(a: Auftrag): string | null {
  const istGolden = a.environment === 'GOLDEN';
  if (istGolden) return a.projectId ? `/projekte/${a.projectId}/ai-completion` : null;
  return `/auftraege/${a.id}`;
}

function dauerSekunden(a: Auftrag): number | null {
  if (!a.finishedAt) return null;
  return Math.floor(
    (new Date(a.finishedAt).getTime() - new Date(a.createdAt).getTime()) / 1000,
  );
}

function dauer(a: Auftrag): string {
  const sekunden = dauerSekunden(a);
  if (sekunden === null) return '—';

  if (sekunden < 60) return `${sekunden} s`;
  return `${Math.floor(sekunden / 60)} min`;
}
